import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  WashingMachine, 
  Wind, 
  Wifi, 
  Clock, 
  MapPin, 
  Phone, 
  Mail,
  ChevronRight,
  Sparkles,
  Timer,
  CreditCard,
  Coffee,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="Lavandaria de Corroios" 
              className="h-16 w-auto"
              data-testid="header-logo"
            />
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('servicos')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-services"
            >
              Serviços
            </button>
            <button 
              onClick={() => scrollToSection('precos')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-prices"
            >
              Preços
            </button>
            <button 
              onClick={() => scrollToSection('como-funciona')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-how-it-works"
            >
              Como Funciona
            </button>
            <button 
              onClick={() => scrollToSection('contactos')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-contacts"
            >
              Contactos
            </button>
          </nav>

          <Button 
            onClick={() => scrollToSection('precos')}
            className="hidden md:flex bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6"
            data-testid="header-cta"
          >
            Ver Preços
          </Button>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-sky-50 via-white to-white pt-20" data-testid="hero-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-5rem)] py-12 lg:py-0">
          {/* Text Content */}
          <motion.div 
            className="order-2 lg:order-1 text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Aberto 365 dias por ano
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-none mb-6">
              Roupa Fresca,
              <br />
              <span className="text-gradient">Vida Leve.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg">
              A sua lavandaria self-service de confiança em Corroios. 
              Aberto todos os dias, das <strong className="text-slate-800">07h às 23h</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('precos')}
                className="btn-primary text-lg h-14 px-8"
                data-testid="hero-cta-prices"
              >
                Ver Preços
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => scrollToSection('contactos')}
                variant="outline"
                className="btn-secondary text-lg h-14 px-8"
                data-testid="hero-cta-location"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Como Chegar
              </Button>
            </div>

            {/* Quick info badges */}
            <div className="flex flex-wrap gap-4 mt-10">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-5 h-5 text-sky-500" />
                <span className="text-sm font-medium">07h - 23h</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Wifi className="w-5 h-5 text-teal-500" />
                <span className="text-sm font-medium">Wi-Fi Grátis</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <CreditCard className="w-5 h-5 text-sky-500" />
                <span className="text-sm font-medium">Pagamento Fácil</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div 
            className="order-1 lg:order-2 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-sky-200 to-teal-200 rounded-3xl opacity-30 blur-2xl"></div>
              <img 
                src="https://scontent.flis5-4.fna.fbcdn.net/v/t1.6435-9/120765666_3342711229181643_4445990199933064571_n.jpg?_nc_cat=109&ccb=1-7&_nc_sid=9eae26&_nc_ohc=jpg-2XTPrNkQ7kNvwEPNaNF&_nc_oc=AdnUdhqZtY1aN8hq564vmEaCBKdBQ27PB0fQf0iUnxNrMlzt8EHRXqfgmMlK9jFUo-k&_nc_zt=23&_nc_ht=scontent.flis5-4.fna&_nc_gid=r9FwXIhKoJbw5imVEotGbA&oh=00_AfvuINYNx25QSrGExAo8wcp0senRi_yMsj9IajvU5-v9rw&oe=69ADC1CA"
                alt="Lavandaria de Corroios - Interior"
                className="relative w-full h-auto rounded-3xl shadow-2xl object-cover aspect-[4/3]"
                data-testid="hero-image"
              />
              
              {/* Floating card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Timer className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Lavagem completa em</p>
                    <p className="text-lg font-bold text-slate-900">~35 minutos</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section - Bento Grid
const ServicesSection = () => {
  const services = [
    {
      title: "Lavagem Premium",
      desc: "Máquinas de alta capacidade (9kg, 12kg, 20kg). Detergente e amaciador incluídos em todos os programas.",
      icon: WashingMachine,
      span: "md:col-span-2 md:row-span-2",
      style: "bg-sky-50 border-sky-100 hover:border-sky-200",
      iconBg: "bg-sky-500"
    },
    {
      title: "Secagem Rápida",
      desc: "Secadores industriais que deixam a roupa macia e pronta a guardar.",
      icon: Wind,
      span: "md:col-span-1 md:row-span-1",
      style: "bg-white border-slate-100 hover:border-slate-200",
      iconBg: "bg-teal-500"
    },
    {
      title: "Wi-Fi Grátis",
      desc: "Navegue enquanto espera pela sua roupa.",
      icon: Wifi,
      span: "md:col-span-1 md:row-span-1",
      style: "bg-teal-50 border-teal-100 hover:border-teal-200",
      iconBg: "bg-teal-500"
    }
  ];

  return (
    <section id="servicos" className="section-spacing bg-white" data-testid="services-section">
      <div className="container-custom">
        <motion.div 
          className="text-left mb-12 md:mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <span className="font-accent text-sky-500 font-semibold uppercase tracking-wider text-sm">
            Serviços
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mt-3">
            Tudo o que precisa,
            <br />
            <span className="text-slate-400">num só lugar.</span>
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className={`${service.span}`}
              variants={fadeInUp}
            >
              <Card 
                className={`h-full border-2 ${service.style} card-hover cursor-default`}
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-6 md:p-8 h-full flex flex-col">
                  <div className={`w-14 h-14 ${service.iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed flex-grow">
                    {service.desc}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const pricesWithCard = [
    { service: "Lavagem 9kg", price: "4,50€", details: "Com cartão cliente", popular: true },
    { service: "Lavagem 12kg", price: "6,50€", details: "Com cartão cliente" },
    { service: "Lavagem 20kg", price: "10,00€", details: "Com cartão cliente" }
  ];

  const pricesWithoutCard = [
    { service: "Lavagem 9kg", price: "5,00€", details: "Sem cartão" },
    { service: "Lavagem 12kg", price: "7,00€", details: "Sem cartão" },
    { service: "Lavagem 20kg", price: "11,00€", details: "Sem cartão" }
  ];

  return (
    <section id="precos" className="section-spacing bg-slate-50" data-testid="pricing-section">
      <div className="container-custom">
        <motion.div 
          className="text-left mb-12 md:mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <span className="font-accent text-sky-500 font-semibold uppercase tracking-wider text-sm">
            Preços
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mt-3">
            Transparentes e
            <br />
            <span className="text-slate-400">acessíveis.</span>
          </h2>
        </motion.div>

        {/* With Card Prices */}
        <motion.div
          className="mb-8"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Com Cartão Cliente</h3>
            <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">Poupe mais!</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {pricesWithCard.map((item, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card 
                  className={`relative border-2 bg-white card-hover ${item.popular ? 'border-sky-500 shadow-lg shadow-sky-100' : 'border-slate-100'}`}
                  data-testid={`price-card-with-${index}`}
                >
                  {item.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-sky-500 text-white text-xs font-semibold rounded-full">
                      Popular
                    </div>
                  )}
                  <CardContent className="p-6 md:p-8 text-center">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      {item.service}
                    </h3>
                    <p className="text-4xl md:text-5xl font-bold text-sky-600 mb-2">
                      {item.price}
                    </p>
                    <p className="text-sm text-slate-500">
                      {item.details}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Without Card Prices */}
        <motion.div
          className="mb-8"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-400 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Sem Cartão Cliente</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {pricesWithoutCard.map((item, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card 
                  className="relative border-2 bg-white card-hover border-slate-100"
                  data-testid={`price-card-without-${index}`}
                >
                  <CardContent className="p-6 md:p-8 text-center">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      {item.service}
                    </h3>
                    <p className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">
                      {item.price}
                    </p>
                    <p className="text-sm text-slate-500">
                      {item.details}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Drying Prices */}
        <motion.div
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
              <Wind className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Secagem</h3>
            <span className="text-slate-500 text-sm">Ciclos de 18 minutos</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 max-w-2xl gap-4 md:gap-6">
            <motion.div variants={fadeInUp}>
              <Card 
                className="relative border-2 bg-teal-50 card-hover border-teal-100"
                data-testid="price-card-drying-with"
              >
                <CardContent className="p-6 md:p-8 text-center">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Com Cartão Cliente
                  </h3>
                  <p className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">
                    1,80€
                  </p>
                  <p className="text-sm text-slate-500">
                    Por ciclo de 18 min
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Card 
                className="relative border-2 bg-white card-hover border-slate-100"
                data-testid="price-card-drying-without"
              >
                <CardContent className="p-6 md:p-8 text-center">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Sem Cartão Cliente
                  </h3>
                  <p className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">
                    2,00€
                  </p>
                  <p className="text-sm text-slate-500">
                    Por ciclo de 18 min
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>

        <motion.p 
          className="text-center text-slate-500 mt-8 text-sm"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          * Todas as lavagens incluem Detergente, Pré-Aditivo, Desinfectante e Amaciador.
          <br />
          Obtenha o seu cartão cliente na central de pagamento!
        </motion.p>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { step: "1", title: "Carregue", desc: "Coloque a roupa na máquina disponível.", icon: WashingMachine },
    { step: "2", title: "Selecione", desc: "Escolha o programa e pague na central.", icon: CreditCard },
    { step: "3", title: "Relaxe", desc: "Aproveite o Wi-Fi ou vá dar uma volta.", icon: Coffee },
    { step: "4", title: "Pronto!", desc: "Roupa limpa e seca em menos de 1 hora.", icon: Sparkles }
  ];

  return (
    <section id="como-funciona" className="section-spacing bg-white" data-testid="how-it-works-section">
      <div className="container-custom">
        <motion.div 
          className="text-left mb-12 md:mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
          whileInView="animate"
          initial="initial"
        >
          <span className="font-accent text-sky-500 font-semibold uppercase tracking-wider text-sm">
            Como Funciona
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mt-3">
            Simples e
            <br />
            <span className="text-slate-400">rápido.</span>
          </h2>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {steps.map((item, index) => (
            <motion.div 
              key={index} 
              className="relative"
              variants={fadeInUp}
            >
              {/* Connector line for desktop */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-sky-200 to-teal-200 -translate-x-1/2 z-0"></div>
              )}
              
              <div className="relative z-10 text-center" data-testid={`step-${index}`}>
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-sky-100 to-teal-50 rounded-full flex items-center justify-center">
                    <item.icon className="w-10 h-10 text-sky-600" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Location & Contact Section
const LocationSection = () => {
  const googleMapsUrl = "https://maps.app.goo.gl/LavandariaCorroios";
  
  return (
    <section id="contactos" className="section-spacing bg-slate-900" data-testid="location-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Info */}
          <motion.div 
            className="order-2 lg:order-1"
            {...fadeInUp}
            viewport={{ once: true }}
            whileInView="animate"
            initial="initial"
          >
            <span className="font-accent text-sky-400 font-semibold uppercase tracking-wider text-sm">
              Contactos
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-3 mb-8">
              Venha visitar-nos
              <br />
              <span className="text-slate-500">em Corroios.</span>
            </h2>

            <div className="space-y-6">
              <a 
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-address"
              >
                <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-sky-500/30 transition-colors">
                  <MapPin className="w-6 h-6 text-sky-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Morada</h4>
                  <p className="text-slate-400 group-hover:text-sky-400 transition-colors">Rua Cidade de Aveiro, 2B</p>
                  <p className="text-slate-400 group-hover:text-sky-400 transition-colors">2855-117 Corroios</p>
                  <p className="text-sky-400 text-sm mt-1 flex items-center gap-1">
                    Ver no Google Maps <ChevronRight className="w-4 h-4" />
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Horário</h4>
                  <p className="text-slate-400">Todos os dias: <span className="text-white font-medium">07:00 - 23:00</span></p>
                  <p className="text-slate-400">Aberto 365 dias por ano</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Telefone / WhatsApp</h4>
                  <a 
                    href="tel:+351968383228" 
                    className="text-slate-400 hover:text-green-400 transition-colors block"
                    data-testid="contact-phone"
                  >
                    +351 968 383 228
                  </a>
                  <a 
                    href="https://wa.me/351968383228" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-medium hover:bg-green-500/30 transition-colors"
                    data-testid="contact-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Enviar mensagem no WhatsApp
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Email</h4>
                  <a 
                    href="mailto:lavandariacorroios@gmail.com" 
                    className="text-slate-400 hover:text-teal-400 transition-colors"
                    data-testid="contact-email"
                  >
                    lavandariacorroios@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map and Image */}
          <motion.div 
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-sky-500/20 to-teal-500/20 rounded-3xl blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3114.8!2d-9.1505!3d38.6284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1934a1234567890%3A0xabcdef1234567890!2sRua%20Cidade%20de%20Aveiro%202B%2C%202855-117%20Corroios!5e0!3m2!1spt-PT!2spt!4v1700000000000"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização Lavandaria de Corroios"
                  className="w-full"
                  data-testid="google-map"
                ></iframe>
              </div>
              <a 
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 rounded-full text-sm font-semibold shadow-lg hover:bg-sky-50 transition-colors"
                data-testid="map-link"
              >
                <MapPin className="w-4 h-4 text-sky-500" />
                Abrir no Google Maps
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 py-12" data-testid="footer">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
              <WashingMachine className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg text-white">Lavandaria de Corroios</span>
          </div>

          <Separator orientation="vertical" className="hidden md:block h-8 bg-slate-800" />

          <p className="text-slate-500 text-sm text-center">
            © {currentYear} Lavandaria de Corroios - Self Service. Todos os direitos reservados.
          </p>

          <div className="flex items-center gap-6">
            <a 
              href="#servicos" 
              className="text-slate-400 hover:text-white text-sm transition-colors"
              data-testid="footer-services"
            >
              Serviços
            </a>
            <a 
              href="#precos" 
              className="text-slate-400 hover:text-white text-sm transition-colors"
              data-testid="footer-prices"
            >
              Preços
            </a>
            <a 
              href="#contactos" 
              className="text-slate-400 hover:text-white text-sm transition-colors"
              data-testid="footer-contacts"
            >
              Contactos
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="noise-overlay">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <PricingSection />
        <HowItWorksSection />
        <LocationSection />
      </main>
      <Footer />
    </div>
  );
};

// App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
